import { publishCollectionChange } from '../../../utils/collectionSync';

const CACHE_PREFIX = 'nlims_cache_';

function hasWindow() {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

function cacheKey(key) {
  return `${CACHE_PREFIX}${key}`;
}

export function readRegistryCollection(key) {
  if (!hasWindow()) return null;
  try {
    const raw = window.localStorage.getItem(cacheKey(key));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function writeRegistryCollection(key, rows) {
  if (!hasWindow()) return rows;
  try {
    window.localStorage.setItem(cacheKey(key), JSON.stringify(rows));
    window.dispatchEvent(
      new CustomEvent('nlims:registry-updated', {
        detail: { key, rows },
      })
    );
    publishCollectionChange(key, { rows });
  } catch (error) {
    console.warn('[RegistryStore] Failed to persist collection', key, error);
  }
  return rows;
}

export function getRegistryCollection(key, fallback = []) {
  const cached = readRegistryCollection(key);
  if (!cached) return fallback;

  const byId = new Map();
  for (const row of fallback) {
    const rowKey = row?.id ?? JSON.stringify(row);
    byId.set(rowKey, row);
  }
  for (const row of cached) {
    const rowKey = row?.id ?? JSON.stringify(row);
    byId.set(rowKey, row);
  }
  return [...byId.values()];
}

export function toOption(value, label = value, extra = {}) {
  return { value, label, ...extra };
}

export function normalizeRegistryValue(value) {
  if (value == null) return '';
  return String(value)
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ');
}

export function matchesRegistryValue(candidate, expected) {
  if (!expected) return true;
  const left = normalizeRegistryValue(candidate);
  const right = normalizeRegistryValue(expected);
  if (!left || !right) return false;
  return left === right || left.includes(right) || right.includes(left);
}

export function optionMatchesSpecies(option, selectedSpecies) {
  if (!selectedSpecies) return true;
  if (!option) return false;
  return [
    option.speciesId,
    option.speciesCode,
    option.speciesLabel,
    option.species,
    option.ownerSpecies,
  ].some((value) => matchesRegistryValue(value, selectedSpecies));
}

export function filterOptionsBySpecies(options = [], selectedSpecies) {
  if (!selectedSpecies) return options;
  return options.filter((option) => optionMatchesSpecies(option, selectedSpecies));
}
